/**
 * Invoice pre-flight check (IPF-3): the response contract of `GET /Document/:id/Preflight` and the pure rules the
 * editor applies to it. No Angular, no I/O, so the merge and stale rules can be tested without a component.
 *
 * Design and owner decisions: `docs/PROMPT_invoice-preflight-availability-check.md`. API contract:
 * `webyto_api-express4-TS-20250331/docs/DOCUMENT_PREFLIGHT_API.md`. The API returns codes and numbers only;
 * the wording is the editor's.
 */

export type PreflightSeverity = 0 | 10 | 100;

export type PreflightCode =
  | 'NOT_COVERED'
  | 'COVERED_BY_INCOMING'
  | 'RESERVATION_EXPIRES_BEFORE_DELIVERY'
  | 'FIGURES_DISAGREE'
  | 'CANNOT_DETERMINE';

/** One other document's claim on the product (a row of the booking list). */
export interface PreflightHolder {
  docId: number;
  docNumber: string | null;
  client: string | null;
  /** Quantity still held by that document. */
  debts: number;
  /** Paid = firm, never lapses. */
  paid: boolean;
  /** ISO day an unpaid reservation ends; null for a paid holder. */
  reservedTill: string | null;
  /** Quantity already moved to the manufacturing depot for that document. Shown, never netted. */
  manued?: number;
  /** `Documents.DOCTYPE` of the holder (an invoice in practice); null = the API did not find it. Added by API `ipf-4.3`, absent from an older API. Used only to link to the document. */
  docType?: number | null;
  /** Unpaid and ending before the delivery date: counted as lapsing. */
  lapses: boolean;
}

/**
 * The vendor's note on an order's lines (`DocItems.DOCITEMTEXT1`), read by the API for display only: `day` when the text was a
 * date, otherwise the words in `text`. It is what the vendor said about shipping from ITS stock, never an arrival date.
 */
export interface PreflightVendorNote {
  day: string | null;
  text: string | null;
}

/**
 * An open in-transit purchase order. The orders of a product are a QUEUE (oldest order first), not a schedule: logistics does
 * not maintain the order's dates, so no date here is an expected arrival (owner, 2026-10-25).
 */
export interface PreflightOrder {
  docId: number;
  docNumber: string | null;
  /** ISO day the order was placed (`DOCDATE`); the API orders the queue by it. Added by API `ipf-4.2`; absent from an older API. */
  orderDate?: string | null;
  /** The vendor's notes on the order's lines, at most 2. Added by API `ipf-4.2`. */
  vendorNotes?: PreflightVendorNote[];
  /** How many further distinct notes the API left out. Added by API `ipf-4.2`. */
  vendorNotesMore?: number;
  /**
   * The order's `DOCVALDATE`. NOT an arrival date and never shown: nobody maintains it. Still in the API response for
   * clients built before `ipf-4.2`; to be removed there once none is deployed.
   */
  valDate: string | null;
  /** Quantity still to arrive on that order. */
  open: number;
}

/**
 * The most recent receipt of the product against a purchase order (a fact, not a forecast). Goods taken in without a
 * purchase order are not seen by it. Added by API `ipf-4.2`.
 */
export interface PreflightLastArrival {
  /** ISO day of the receipt. */
  day: string;
  receiptDocId: number;
  /** `DOCTYPE` of the receipt, 2 or 3. Added by API `ipf-4.3`, absent from an older API. Used only to link to the document. */
  receiptDocType?: number;
  receiptDocNumber: string | null;
  /** Quantity of the product on that receipt. */
  qty: number;
  orderDocId: number;
  orderDocNumber: string | null;
}

export interface PreflightNumbers {
  need: number;
  stock: number;
  sold: number;
  reserved: number;
  ownClaim: number;
  free: number;
  freeFromHolders: number;
  shortfall: number;
  lapsing: number;
  onWay: number;
  /**
   * `stock - others' claims + lapsing + onWay - need`, signed and NOT floored: negative is the deficit nothing in sight
   * covers, zero or more is covered. Added by API `ipf-4.1`; absent from an older API, so every use must cope with it.
   */
  balanceAfterIncoming?: number;
}

/** Where a component's demand comes from: one assembly line group on the invoice (IPF-4). */
export interface BomSource {
  assemblyProdId: number;
  assemblyCode: string;
  /** Units of this component per unit of the assembly. */
  perUnit: number;
  /** Component units demanded through this assembly: its unmet quantity times `perUnit`. */
  qty: number;
  /** Every line of the invoice that stands behind this demand. */
  docItemIds: number[];
}

/** Present on a finding whose need includes demand through an assembly. */
export interface FindingBom {
  /** The product's own unshipped quantity on direct lines (0 for a component-only product). */
  directNeed: number;
  /** Demand added through assemblies; `numbers.need = directNeed + bomNeed`. */
  bomNeed: number;
  via: BomSource[];
}

export interface PreflightFinding {
  code: PreflightCode;
  severity: PreflightSeverity;
  prodId: number;
  prodCode: string;
  docItemIds: number[];
  /** Null for `CANNOT_DETERMINE`. */
  numbers: PreflightNumbers | null;
  holders: PreflightHolder[];
  orders: PreflightOrder[];
  /**
   * The product's last receipt against a purchase order; null = none found; absent = an API older than `ipf-4.2`, in which
   * case nothing is shown (not "none found").
   */
  lastArrival?: PreflightLastArrival | null;
  /** Machine flags, worded by `PREFLIGHT_FLAG_TEXT`; an unknown one is shown as it is. */
  flags: string[];
  bom?: FindingBom;
}

export interface PreflightResult {
  documentId: number;
  version: string;
  /** Today, ISO day, database clock. */
  asOfDate: string;
  /** The date reservations were judged against: the delivery date when later than today, else today. */
  referenceDate: string;
  /** The highest finding severity: the contribution to `DOCFLAG`. */
  maxSeverity: PreflightSeverity;
  /** Database UTC time of the check. */
  checkedAt: string;
  /** `bomDepth`: `one-level` when the BOM was walked, `none` when not; `bomSkipped` says why (`TOO_MANY_PRODUCTS`). */
  scope: { bomDepth: string; nestedBomNotExpanded: number[]; bomSkipped?: string };
  findings: PreflightFinding[];
}

/** A result together with the document lines it was computed from. */
export interface PreflightState {
  result: PreflightResult;
  /** {@link preflightSignature} of the document at the moment of the check. */
  signature: string;
}

/** The parts of a document the check depends on. Structural, so the editor's `Document` fits. */
export interface PreflightSignatureSource {
  DOCMANCLIID?: number | null;
  /** A `Date` in the model's type, a string at runtime (JSON has no date type). */
  DOCEXPDATE?: Date | string | null;
  items?: { PRODID?: number | null; DOCITEMQTY?: number | null }[] | null;
}

/** An ISO day of a date-ish value, '' when absent or unparseable. */
function isoDay(value: Date | string | null | undefined): string {
  if (!value) { return ''; }
  const d = new Date(value);
  return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

/**
 * A string that changes when something the check depends on changes: the ordered quantity per product, the
 * delivery date (`DOCEXPDATE`) and the company (`DOCMANCLIID`). Quantities are summed per product, as the check
 * does, so splitting one line into two with the same total does not make the result stale.
 */
export function preflightSignature(doc: PreflightSignatureSource | null | undefined): string {
  if (!doc) { return ''; }
  const perProduct = new Map<number, number>();
  for (const di of doc.items || []) {
    if (di == null || !di.PRODID) { continue; }
    const qty = Number(di.DOCITEMQTY);
    perProduct.set(di.PRODID, (perProduct.get(di.PRODID) || 0) + (Number.isFinite(qty) ? qty : 0));
  }
  const lines = [...perProduct.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([prodId, qty]) => `${prodId}:${Math.round(qty * 10000) / 10000}`)
    .join(',');
  return `${doc.DOCMANCLIID ?? ''}|${isoDay(doc.DOCEXPDATE)}|${lines}`;
}

/** True while the result still describes the document as it is now. */
export function preflightIsFresh(state: PreflightState | null | undefined, currentSignature: string): boolean {
  return !!state && state.signature === currentSignature;
}

/**
 * The pre-flight contribution to `DOCFLAG`: the result's severity while it is fresh, 0 once any quantity, product,
 * delivery date or company has changed since the check (finding F2: a stale result must not keep a flag raised).
 */
export function preflightContribution(state: PreflightState | null | undefined, currentSignature: string): PreflightSeverity {
  return preflightIsFresh(state, currentSignature) ? state.result.maxSeverity : 0;
}

/** The merge rule: `DOCFLAG = max(editor flag, pre-flight contribution)`. */
export function mergeDocFlag(editorFlag: number | null | undefined, contribution: number): number {
  const editor = Number(editorFlag);
  return Math.max(Number.isFinite(editor) ? editor : 0, contribution || 0);
}

/** What the "На печать" gate does with the editor's row checks and the pre-flight result (IPF-5). */
export interface ExportGate {
  /** Show the confirmation dialog at all. Never a hard block: the user can always continue. */
  confirm: boolean;
  /** The user must type the document number. Plain Continue when false. */
  typed: boolean;
  /** Which sources raised it, to choose the wording. */
  source: 'none' | 'rows' | 'preflight' | 'both';
}

/**
 * One gate for both sources (owner decisions Q19-Q21). Highlighted rows keep today's typed confirmation; a serious
 * pre-flight finding (100) also needs the typed confirmation; a minor one (10) gets a plain Continue. A pre-flight
 * result of 0 (clean, or only "cannot determine") adds nothing, so the export of a clean invoice is unchanged.
 */
export function exportGate(rowsHighlighted: boolean, preflightSeverity: number): ExportGate {
  const sev = Number.isFinite(Number(preflightSeverity)) ? Number(preflightSeverity) : 0;
  const byPreflight = sev > 0;
  return {
    confirm: rowsHighlighted || byPreflight,
    typed: rowsHighlighted || sev >= 100,
    source: rowsHighlighted && byPreflight ? 'both' : rowsHighlighted ? 'rows' : byPreflight ? 'preflight' : 'none',
  };
}
